// Web Push subscription. Spec 8.5 and 8.6.
//
// On iOS this only works once the PWA has been added to the home screen. If
// nothing arrives, that is almost always the reason. So the permission
// prompt happens after an explicit tap rather than on load, because Safari
// refuses it silently otherwise.
import { useCallback, useEffect, useState } from 'react';

const b64ToBytes = (base64: string): Uint8Array => {
  const pad = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
};

export type PushState = 'unsupported' | 'not-configured' | 'idle' | 'granted' | 'denied';

export function usePush(workerUrl: string) {
  const [state, setState] = useState<PushState>('idle');
  const [installedToHomeScreen, setInstalled] = useState(true);

  useEffect(() => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      setState('unsupported');
      return;
    }
    // iOS requires standalone display mode for push to work at all.
    const standalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      (navigator as Navigator & { standalone?: boolean }).standalone === true;
    const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
    setInstalled(!isIos || standalone);

    if (Notification.permission === 'granted') setState('granted');
    else if (Notification.permission === 'denied') setState('denied');
  }, []);

  const subscribe = useCallback(async () => {
    try {
      const keyRes = await fetch(`${workerUrl}/vapid`);
      const { publicKey } = (await keyRes.json()) as { publicKey: string | null };
      if (!publicKey) {
        setState('not-configured');
        return;
      }

      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setState(permission === 'denied' ? 'denied' : 'idle');
        return;
      }

      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: b64ToBytes(publicKey) as BufferSource,
      });

      // Idempotent on the server, so re-posting on every open is fine and
      // is the simplest way to handle subscription rotation. Spec 8.6.
      await fetch(`${workerUrl}/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sub),
      });
      setState('granted');
    } catch {
      setState('idle');
    }
  }, [workerUrl]);

  const unsubscribe = useCallback(async () => {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return;
    await fetch(`${workerUrl}/subscribe?endpoint=${encodeURIComponent(sub.endpoint)}`, {
      method: 'DELETE',
    });
    await sub.unsubscribe();
    setState('idle');
  }, [workerUrl]);

  return { state, installedToHomeScreen, subscribe, unsubscribe };
}
